import { useTranslation } from 'react-i18next';
import { cn, isBulkOrder, SiteConfig } from '@utils';
import type { Product, Discount, UnavailableProduct } from '@types';
import { QuantitySelector } from '@components/common/QuantitySelector';
import { ProductPrice } from '@components/common/ProductPrice';
import { InfoNotification } from '@components/ProductList/components/InfoNotification';
import { buildNotificationData } from '@components/ProductList/utils';
import { useState } from 'react';
import './style.scss';

import { DEFAULT_SITE_ID, AEM_RESOURCE_BASE_URL, DEFAULT_IMAGE } from '@config/constants';
import { ProductErrorSection } from './ProductErrorSection';
import { ProductEbookNote } from './ProductEbookNote';

type ProductItemProps = {
  product: Product;
  currency: string;
  isFreeTrial: boolean;
  discount?: Discount;
  isRestrictedProduct?: boolean;
  restrictionReason?: UnavailableProduct['reason']
  onQuantityChange: (qty: number) => void;
  onRemove: () => void;
};

const ProductItem = ({
  product,
  currency,
  discount,
  isFreeTrial,
  restrictionReason,
  isRestrictedProduct,
  onQuantityChange,
  onRemove,
}: ProductItemProps) => {
  const { t } = useTranslation();
  const { siteId } = SiteConfig.getConfig();
  const isDefaultSite = DEFAULT_SITE_ID === siteId;
  const productInfoUrl = `${AEM_RESOURCE_BASE_URL}/${product.url}`;

  const bulkOrderInfo = isBulkOrder(product.maId);

  const renderProductTitle = (isClickable: boolean) => {
    const className = isClickable
      ? 'product__info-title'
      : 'product__info-title non-clickable';
    return isClickable ? (
      <a className={className} href={productInfoUrl}>
        {product.title}
      </a>
    ) : (
      <span className={className}>{product.title}</span>
    );
  };

  const qtyInfoMessage = product.physicalProduct
    ? t('cart.productList.physicalProductTooltip')
    : t('cart.productList.digitalProductTooltip');

  const notificationData = buildNotificationData({
    discount,
    isFreeTrial,
    status: product.status,
    productLink: productInfoUrl,
    printOnDemand: product.printOnDemand,
    licenseAgreementInfo: product.licenseAgreementInfo,
  });

  const [imgSrc, setImgSrc] = useState(!product.image
    ? DEFAULT_IMAGE : product.image);

  const handleImageError = () => {
    setImgSrc(DEFAULT_IMAGE);
  };

  const imgDivClass = `${imgSrc === DEFAULT_IMAGE ? 'default-img-bcground' : ''}`;
  const imgClas = `${imgSrc === DEFAULT_IMAGE ? 'default-img' : 'product__image'}`;

  return (
    <li
      key={product.uid}
      data-testid="product-item"
      className={cn('product', { 'product--updating': product.updating })}
    >
      <div className={imgDivClass}>
        <img
          className={imgClas}
          src={imgSrc}
          alt="Product"
          loading="lazy"
          onError={handleImageError}
        />
      </div>
      <div className="product__details">
        <div className="product__info-container">
          <div className="product__info">
            {renderProductTitle(isDefaultSite)}
            <div className="product__info-format">
              <span>{`${t('cart.productList.format')}: `}</span>
              <span>{product.format}</span>
            </div>
            {product.brand && (
              <div className="product__info-brand">{product.brand}</div>
            )}
            {product.courseId && (
              <div className="product__info-course">
                <span>{`${t('cart.productList.courseId')}: `}</span>
                <span>{product.courseId}</span>
              </div>
            )}
            {product.instructors && (
              <div className="product__info-instructors">
                <span>{`${t('cart.productList.instructors')}: `}</span>
                <span>{product.instructors.join(', ')}</span>
              </div>
            )}
            <div className="product__info-qty">
              <span>{`${t('cart.productList.qty')}: `}</span>
              <QuantitySelector
                qty={product.qty}
                onChange={onQuantityChange}
                onRemove={onRemove}
                disabledIncrement={
                  !product.physicalProduct || product.qty >= bulkOrderInfo.maxQty
                }
                minQty={bulkOrderInfo.isAllowed ? 0 : 1}
                isBulkOrder={bulkOrderInfo.isAllowed}
                infoMessage={qtyInfoMessage}
              />
            </div>
          </div>
          <div className="product__price">
            <ProductPrice
              currency={currency}
              discount={discount}
              amount={product.amount}
              discountAmount={product.discountAmount}
            />
          </div>
        </div>
        <div>
          <button
            type="button"
            onClick={onRemove}
            className="product__info-remove"
            data-testid="product-item-remove-btn"
          >
            {t('cart.productList.remove')}
          </button>
        </div>

        {product?.format?.toLowerCase() === 'e-book' && (
          <ProductEbookNote />
        )}

        {notificationData && <InfoNotification {...notificationData} />}
        {(isRestrictedProduct || product.updateError) && (
          <div className="product__info-error" data-testid="product-item-error">
            <ProductErrorSection
              restrictionReason={restrictionReason}
              isUpdateError={product.updateError}
            />
          </div>
        )}
      </div>
    </li>
  );
};

export { ProductItem };
